/**
 * The discussion on one item: a thread with avatars and @mentions (stored as
 * user ids, shown as chips), a composer with an @ picker, and read marks. An
 * @mention emails that person and lights their bell. Readiness is a plan
 * function: only the plan's own people are here.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, fmtWhen } from './api.js';
import { Card, Button, Empty } from './ui.jsx';
import { AskComplianceButton } from './AskCompliance.jsx';

const TOKEN = /@\[([^\]]{1,120})\]\(user:([^)\s]{1,64})\)/gi;
const COLORS = ['bg-orange-100 text-orange-800', 'bg-sky-100 text-sky-800', 'bg-emerald-100 text-emerald-800', 'bg-violet-100 text-violet-800', 'bg-amber-100 text-amber-800', 'bg-rose-100 text-rose-800', 'bg-teal-100 text-teal-800'];
const hue = (s) => COLORS[[...String(s || '')].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length];
const initials = (name) => String(name || '?').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
export function Avatar({ name, size = 'w-8 h-8 text-xs' }) {
  return <span className={`inline-flex items-center justify-center rounded-full font-bold ${size} ${hue(name)}`} title={name}>{initials(name)}</span>;
}

/** Render a body: @[Name](user:id) becomes a chip; everything else is plain text with line breaks. */
export function Body({ text }) {
  const parts = [];
  let last = 0; const s = String(text || '');
  for (const m of s.matchAll(TOKEN)) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    parts.push(<span key={`${m.index}`} className="inline-flex items-center px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-800 ring-1 ring-orange-200 text-[12px] font-semibold mx-0.5">@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return <span className="whitespace-pre-wrap break-words">{parts}</span>;
}

let PEOPLE_CACHE = null;
function usePeople() {
  const [people, setPeople] = useState(PEOPLE_CACHE || []);
  useEffect(() => { if (PEOPLE_CACHE) return; api('/people/mentionable').then((d) => { if (d?.success) { PEOPLE_CACHE = d.people; setPeople(d.people); } }); }, []);
  return people;
}

export default function Discussion({ item, boot, initialComments = null }) {
  const [comments, setComments] = useState(initialComments); // initialComments: the render harness only
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [pick, setPick] = useState(null); // { start, query, index }
  const people = usePeople();
  const box = useRef(null);
  const me = boot.user?.id;

  useEffect(() => {
    if (initialComments) return undefined;
    let alive = true;
    api(`/items/${item.id}/comments`).then((d) => { if (alive) setComments(d?.success ? d.comments : []); });
    return () => { alive = false; };
  }, [item.id, initialComments]);

  const suggestions = useMemo(() => {
    if (!pick) return [];
    const q = pick.query.toLowerCase();
    return people.filter((p) => p.id !== me && (!q || p.name.toLowerCase().includes(q) || String(p.email || '').toLowerCase().startsWith(q))).slice(0, 6);
  }, [pick, people, me]);

  const onChange = (e) => {
    const v = e.target.value; setText(v);
    const caret = e.target.selectionStart || v.length;
    const before = v.slice(0, caret);
    const m = /(^|\s)@([^\s@\]]{0,40})$/.exec(before);
    setPick(m ? { start: caret - m[2].length - 1, query: m[2], index: 0 } : null);
  };
  const insert = (p) => {
    if (!pick) return;
    const token = `@[${p.name}](user:${p.id}) `;
    const caret = box.current?.selectionStart || text.length;
    const next = text.slice(0, pick.start) + token + text.slice(caret);
    setText(next); setPick(null);
    requestAnimationFrame(() => { if (box.current) { const pos = pick.start + token.length; box.current.focus(); box.current.setSelectionRange(pos, pos); } });
  };
  const onKey = (e) => {
    if (pick && suggestions.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setPick({ ...pick, index: (pick.index + 1) % suggestions.length }); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setPick({ ...pick, index: (pick.index - 1 + suggestions.length) % suggestions.length }); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insert(suggestions[pick.index]); return; }
      if (e.key === 'Escape') { setPick(null); return; }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); post(); }
  };
  const post = async () => {
    const body = text.trim(); if (!body || busy) return;
    setBusy(true); setErr(null);
    const d = await api(`/items/${item.id}/comments`, { method: 'POST', body: { body } });
    setBusy(false);
    if (!d?.success) { setErr(d?.message || 'Could not post'); return; }
    setComments(d.comments); setText(''); setPick(null);
  };

  const onIt = Array.isArray(item.assignee_names) ? item.assignee_names : [];
  const compliance = !!boot.is_compliance;
  return (
    <Card title="Discussion" right={<span className="text-xs text-slate-500">{onIt.length ? `${onIt.length} ${onIt.length === 1 ? 'person' : 'people'} on this item, plus compliance` : 'you and compliance'} · type @ to bring someone in</span>}>
      {!compliance && (
        <div className="mb-3 flex items-center justify-between gap-3 flex-wrap rounded-xl border border-slate-200 bg-slate-50 px-3 py-2" title="Only compliance can change who an item is assigned to. Ask, and they decide.">
          <span className="text-xs text-slate-600">Feel this item is not yours? Ask compliance to assign it to someone else.</span>
          <AskComplianceButton topicKey={item.topic_key} itemId={item.id} questionKind="reassign" label={`${item.cms_ref} · ${item.topic_cms_title || item.topic_title || ''}`} preset={`${item.cms_ref} is not mine. It should go to `} />
        </div>
      )}
      {comments === null ? <Empty>Loading…</Empty> : comments.length === 0 ? (
        <div className="text-sm text-slate-500 py-3">Nothing yet. Ask a question, say what you sent, or bring in a teammate with @.</div>
      ) : (
        <ul className="space-y-3">
          {comments.map((c) => (
            <li key={c.id} className="flex gap-3">
              <Avatar name={c.author} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline gap-2 flex-wrap"><span className="text-sm font-semibold text-slate-800">{c.author}{c.user_id === me ? ' (you)' : ''}</span><span className="text-[11px] text-slate-400">{fmtWhen(c.created_at)}{c.via === 'email' ? ' · by email' : ''}</span></div>
                <div className="text-sm text-slate-800 mt-0.5 leading-relaxed"><Body text={c.body} /></div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-4 relative">
        <div className="flex gap-3">
          <Avatar name={boot.user?.name} />
          <div className="flex-1 min-w-0">
            <textarea ref={box} rows={text.length > 80 ? 4 : 2} value={text} onChange={onChange} onKeyDown={onKey} placeholder="Write to the people on this item. @ a name to bring someone in." className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-200" />
            {pick && suggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-72 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
                {suggestions.map((p, i) => (
                  <button key={p.id} type="button" onMouseDown={(e) => { e.preventDefault(); insert(p); }} className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${i === pick.index ? 'bg-orange-50' : 'hover:bg-slate-50'}`}>
                    <Avatar name={p.name} size="w-6 h-6 text-[10px]" /><span className="font-medium text-slate-800">{p.name}</span><span className="text-xs text-slate-400 truncate">{p.email}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <span className="text-[11px] text-slate-500">An @ mention emails that person and lights their bell. Ctrl+Enter posts.</span>
              <Button onClick={post} disabled={busy || !text.trim()}>{busy ? 'Posting…' : 'Post'}</Button>
            </div>
            {err && <div className="text-xs text-rose-600 mt-1">{err}</div>}
          </div>
        </div>
      </div>
    </Card>
  );
}
